import { Button, ButtonGroup } from '@material-ui/core'
import { isEmpty } from 'lodash'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'

import {
  cancelSingleSession,
  changeExcelBatch,
  pauseExcelEnrolment,
  resumeExcelEnrolment,
  terminateProgramEnrolment,
  terminateProgramEnrolmentImmediately
} from 'api'
import BaseEntity from 'app/components/BaseEntity'
import CancelModal from 'app/components/CancelEntity/CancelModal'
import { useStyles } from 'app/components/generic/Button'
import { excelEnrolmentActions } from 'app/config'
import constants from 'app/constants'
import { eventAlert, eventEmitter } from 'app/helpers'
import { useAccess } from 'app/hooks/useAccess'
import {
  fetchExcelEnrolmentData,
  updateClassData
} from 'app/store/actions/classEntity'

import ChangeTimingsForm from './components/ChangeTimingsForm'
import PauseForm from './components/PauseForm'
import ResumeForm from './components/ResumeForm'
import TerminateImmediatelyForm from './components/TerminateImmediatelyForm'
import { columns } from './config'

import MasterModal from '../components/MasterModal'

const ExcelEnrolment = () => {
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [isCancelModalOpen, setIsCancelModalOpen] = React.useState(false)
  const [action, setAction] = React.useState('')
  const [cancelId, setCancelId] = React.useState('')
  const [editIndex, setEditIndex] = React.useState(null)
  const excelEnrolmentData = useSelector(
    (state) => state.classEntityReducer.excelEnrolmentData
  )
  const dispatch = useDispatch()
  // Terminate Immediately is Super-Admin-only. This is a UI gate: the endpoint
  // itself stays callable by any admin, matching the Super-Admin-only batch
  // course editor (see Batch.js).
  const { isSuperAdmin } = useAccess()

  const openModal = React.useCallback((action, dataIndex) => {
    setIsModalOpen(true)
    setAction(action)
    setEditIndex(dataIndex)
  }, [])

  const closeModal = () => {
    setIsModalOpen(false)
  }

  const closeCancelModal = () => {
    setIsCancelModalOpen(false)
  }

  const onActionButtonClick = (e, action, dataIndex) => {
    if (action === 'cancel') {
      setIsCancelModalOpen(true)
    } else {
      openModal(action, dataIndex)
    }
  }

  const refreshList = () => {
    const queries = JSON.parse(localStorage.getItem('filterQueries'))
    dispatch(
      fetchExcelEnrolmentData(
        { ...queries },
        false,
        false,
        constants.EXCEL_ENROLMENT
      )
    )
  }

  const onSaveClick = async (newData, action) => {
    // Excel rows come from /oh_programs/enrolments, so `uuid` is the
    // ProgramEnrolment uuid the program-level endpoints expect.
    const { uuid: programEnrolmentUuid } = excelEnrolmentData.data[editIndex]
    try {
      if (action === 'terminate_enrolment') {
        await terminateProgramEnrolment({
          enrolment_uuid: programEnrolmentUuid,
          notes: newData.notes,
          endDate: newData.end_date,
          reason: newData.reason
        })

        eventEmitter.emit(constants.CLOSE_MODAL_FORM)
        eventAlert('Enrolment Terminated Successfully', constants.SUCCESS)
        closeModal()
      } else if (action === 'terminate_immediately') {
        const result = await terminateProgramEnrolmentImmediately({
          enrolment_uuid: programEnrolmentUuid,
          notes: newData.notes,
          reason: newData.reason
        })

        eventEmitter.emit(constants.CLOSE_MODAL_FORM)
        const cancelled = result?.data?.sessions_cancelled ?? 0
        eventAlert(
          `Enrolment terminated with immediate effect — ${cancelled} upcoming session(s) cancelled.`,
          constants.SUCCESS
        )
        closeModal()
      } else if (action === 'pause_enrolment') {
        await pauseExcelEnrolment({
          program_enrolment: programEnrolmentUuid,
          month: newData.month,
          year: newData.year
        })

        eventEmitter.emit(constants.CLOSE_MODAL_FORM)
        eventAlert('Enrolment Paused Successfully', constants.SUCCESS)
        closeModal()
      } else if (action === 'resume_enrolment') {
        const resumeRes = await resumeExcelEnrolment({
          program_enrolment: programEnrolmentUuid,
          resume_date: newData.resume_date
        })
        // A journey resume with an insufficient wallet returns a payment link.
        const paymentLink = resumeRes?.data?.payment_link

        eventEmitter.emit(constants.CLOSE_MODAL_FORM)
        if (paymentLink) {
          window.open(paymentLink, '_blank')
          eventAlert(
            'Payment required to resume — opening the payment link.',
            constants.SUCCESS
          )
        } else {
          eventAlert('Enrolment Resumed Successfully', constants.SUCCESS)
        }
        closeModal()
      } else if (action === 'change_timings') {
        await changeExcelBatch({
          program_enrolment: programEnrolmentUuid,
          new_batch: newData.new_batch
        })

        eventEmitter.emit(constants.CLOSE_MODAL_FORM)
        eventAlert('Timings Changed Successfully', constants.SUCCESS)
        closeModal()
      } else if (action === 'cancel_sessions') {
        const response = await cancelSingleSession({
          sessionId: cancelId,
          queries: {
            attendance: {
              reason: !isEmpty(newData?.notes?.trim())
                ? `${newData.reason}##${newData.notes}`
                : `${newData.reason}`
            },
            reason_for_using_alfred: newData.reason_for_using_alfred
          }
        })
        if (response.success) {
          eventEmitter.emit(constants.CLOSE_MODAL_FORM)
          eventAlert('Session canceled Successfully', constants.SUCCESS)
        }
        closeCancelModal()
      }

      refreshList()
    } catch (error) {
      console.log(error)
      eventEmitter.emit(constants.MODAL_FORM_SAVE_ERROR, error)
      eventAlert(error.message, constants.ERROR)
    }
  }
  const classes = useStyles()
  const modifiedColumns = React.useMemo(() => {
    return [
      ...columns,
      {
        name: '',
        label: 'actions',
        disabledInForm: true,
        options: {
          disableFilter: true,
          filter: false,
          sort: false,
          customBodyRenderLite: (dataIndex) => {
            const { status, is_termination_requested } =
              excelEnrolmentData?.data[dataIndex] || {}

            return (
              status !== 'terminated' &&
              status !== 'expired' && (
                <ButtonGroup>
                  {Object.keys(excelEnrolmentActions)
                    .filter((action) => {
                      const isPaused = status === 'paused'
                      if (
                        action === 'terminate_enrolment' &&
                        is_termination_requested
                      )
                        return false
                      // Super-Admin-only escape hatch. Hidden for everyone
                      // else, and pointless once termination is already
                      // requested for this row.
                      if (action === 'terminate_immediately')
                        return isSuperAdmin && !is_termination_requested
                      // Pause is available for active, upcoming AND paused
                      // enrolments (the eligible months differ by status, and an
                      // already-paused month is greyed out). Change Timings only
                      // applies while active; Resume only while paused.
                      if (action === 'change_timings' && isPaused) return false
                      if (action === 'resume_enrolment' && !isPaused)
                        return false
                      return true
                    })
                    .map((action, index) => {
                      return (
                        <Button
                          key={'ExcelEnrolment' + index}
                          title={excelEnrolmentActions[action]}
                          className={classes.button}
                          onClick={(e) => {
                            onActionButtonClick(e, action, dataIndex)
                          }}
                        >
                          {excelEnrolmentActions[action]}
                        </Button>
                      )
                    })}
                </ButtonGroup>
              )
            )
          }
        }
      }
    ]
  }, [excelEnrolmentData, isSuperAdmin])

  const activeRow = excelEnrolmentData.data[editIndex] || {}

  return (
    <>
      <BaseEntity
        entity={constants.EXCEL_ENROLMENT}
        label={constants.EXCEL_ENROLMENT_LABEL}
        columns={modifiedColumns}
        data={excelEnrolmentData}
        fetchData={fetchExcelEnrolmentData}
        updateEntityData={updateClassData}
        createDependency
        disableDelete
        readOnly
      />
      {action === 'pause_enrolment' ? (
        <PauseForm
          key={activeRow.uuid}
          isModalOpen={isModalOpen}
          formData={activeRow}
          onSaveClick={onSaveClick}
          closeModal={closeModal}
        />
      ) : action === 'resume_enrolment' ? (
        <ResumeForm
          key={activeRow.uuid}
          isModalOpen={isModalOpen}
          formData={activeRow}
          onSaveClick={onSaveClick}
          closeModal={closeModal}
        />
      ) : action === 'terminate_immediately' ? (
        <TerminateImmediatelyForm
          key={activeRow.uuid}
          isModalOpen={isModalOpen}
          formData={activeRow}
          onSaveClick={onSaveClick}
          closeModal={closeModal}
        />
      ) : action === 'change_timings' ? (
        <ChangeTimingsForm
          key={activeRow.uuid}
          isModalOpen={isModalOpen}
          formData={activeRow}
          onSaveClick={onSaveClick}
          closeModal={closeModal}
        />
      ) : (
        <MasterModal
          isModalOpen={isModalOpen}
          onSaveClick={onSaveClick}
          closeModal={closeModal}
          action={action}
          data={activeRow}
          onActionButtonClick={onActionButtonClick}
          setCancelId={setCancelId}
          isProgramEnrolment={true}
        />
      )}
      <CancelModal
        isModalOpen={isCancelModalOpen}
        onSaveClick={onSaveClick}
        closeModal={closeCancelModal}
        action={action}
        cancelActionType="cancel_sessions"
        requireAlfredReason
      />
    </>
  )
}

export default ExcelEnrolment
